import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { Container } from "@mui/system";
import Timeline from "@mui/lab/Timeline";
import {
  TimelineItem,
  timelineItemClasses,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
} from "@mui/lab";
import { FormEvent, useEffect, useRef, useState } from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AddIcon from "@mui/icons-material/Add";
import { useMutation, useQuery } from "@tanstack/react-query";
import getUserEvents from "./api/getUserEvents";
import QueryKeys from "./types/QueryKeys";
import addUserEvent from "./api/addUserEvent";
import UserEvent from "./types/UserEvent";
import queryClient from "./utils/queryClient";
import getDateKey from "./utils/getDateKey";

export class Timestamp {
  constructor(public title: string, public date: Date = new Date()) {}
}

export default function App() {
  const [popupIsOpen, setPopupIsOpen] = useState(false);
  const [newUserEventTitle, setNewUserEventTitle] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());
  const inputRef = useRef<HTMLInputElement>(null);

  const { isLoading, data: userEvents = [] } = useQuery({
    queryKey: [QueryKeys.USER_EVENTS, getDateKey(currentDate)],
    queryFn: () =>
      getUserEvents(currentDate, "me").then((res) => (res === null ? [] : res)),
  });

  const addUserEventMutation = useMutation({
    onSuccess: (newEvents) =>
      queryClient.setQueryData(
        [QueryKeys.USER_EVENTS, getDateKey(newEvents[0].date)],
        newEvents
      ),
    mutationFn: (newUserEvent: UserEvent) => addUserEvent(newUserEvent, "me"),
  });

  const handleAddUserEvent = (e: FormEvent) => {
    e.preventDefault();
    addUserEventMutation.mutate(new UserEvent(newUserEventTitle));
    setNewUserEventTitle("");
    setPopupIsOpen(false);
  };

  const selectDate = () => {
    if (inputRef.current) {
      inputRef.current.showPicker();
    }
  };

  return (
    <>
      <AppBar position="sticky">
        <Toolbar variant="dense">
          <Typography variant="body1" component="div" sx={{ flexGrow: 1 }}>
            Timeline
          </Typography>
          <Button
            color="inherit"
            endIcon={<CalendarMonthIcon />}
            onClick={selectDate}
          >
            {currentDate.toLocaleDateString("ru", {
              year: "numeric",
              month: "numeric",
              day: "numeric",
            })}
          </Button>
          <input
            style={{
              width: 0,
              height: 0,
              background: "transparent",
              border: "none",
            }}
            ref={inputRef}
            type="date"
            value={currentDate.toISOString().substr(0, 10)}
            onChange={(e) => setCurrentDate(new Date(e.target.value))}
          />
        </Toolbar>
      </AppBar>
      <Box sx={{ position: "fixed", right: "1rem", bottom: "1rem" }}>
        <Fab
          color="primary"
          aria-label="add"
          onClick={() => setPopupIsOpen(true)}
        >
          <AddIcon />
        </Fab>
      </Box>
      <Container maxWidth="sm">
        {isLoading ? (
          <CircularProgress />
        ) : (
          <Timeline
            sx={{
              [`& .${timelineItemClasses.root}::before`]: {
                flex: 0.25,
              },
            }}
          >
            {userEvents
              .sort((a, b) => b.date.getTime() - a.date.getTime())
              .map((userEvent) => (
                <TimelineItem key={userEvent.date.getTime()}>
                  <TimelineSeparator>
                    <TimelineDot />
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent>
                    <Typography variant="h6" component="div">
                      {userEvent.title}
                    </Typography>
                    <Typography component="time">
                      {userEvent.formattedTime}
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              ))}
          </Timeline>
        )}
      </Container>
      <Dialog open={popupIsOpen} onClose={() => setPopupIsOpen(false)}>
        <DialogTitle>Add new timestamp</DialogTitle>
        <form onSubmit={handleAddUserEvent}>
          <DialogContent>
            <TextField
              required
              variant="standard"
              fullWidth
              label="Title"
              inputRef={(input) =>
                input && setTimeout(() => input.focus(), 100)
              }
              value={newUserEventTitle}
              onChange={(e) => setNewUserEventTitle(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button role="submit" onClick={handleAddUserEvent}>
              Add
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
